import { Button, CheckIcon, CloseIcon, Table, TableColumns, Tooltip, UpdateIcon } from '@bp/ui-components';
import dayjs from 'dayjs';
import { ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { EventJobStatus, useEventJobsQuery } from '../../../types/planung-graphql-client-defs';

type SynchronizationsTableType = {
  uuid: string;
  startedAt: string;
  finishedAt: string;
  eventsFrom: string;
  eventsTo: string;
  deleted: number;
  created: number;
  updated: number;
  status: EventJobStatus;
};

type SynchronizationsTableProps = {
  onSync: () => void;
};

export const SynchronizationsTable = ({ onSync }: SynchronizationsTableProps) => {
  const { t } = useTranslation();
  const schoolYear = useSelectedSchoolYear();

  const [{ data: eventJobsData }, refetch] = useEventJobsQuery({
    pause: !schoolYear,
    requestPolicy: 'network-only',
  });

  const hasRunningEventJobs = eventJobsData?.eventJobs.some((job) => job.status === EventJobStatus.Running);

  const data: SynchronizationsTableType[] =
    eventJobsData?.eventJobs.map((job) => {
      return {
        uuid: job.uuid,
        startedAt: dayjs(job.from).format('DD.MM.YYYY, HH:mm:ss'),
        finishedAt: job.to ? dayjs(job.to).format('DD.MM.YYYY, HH:mm:ss') : '',
        created: job.createdEvents,
        deleted: job.deletedEvents,
        updated: job.updatedEvents,
        eventsFrom: job.eventsFrom ? dayjs(job.eventsFrom).format('DD.MM.YYYY') : '',
        eventsTo: job.eventsTo ? dayjs(job.eventsTo).format('DD.MM.YYYY') : '',
        status: job.status,
      };
    }) ?? [];

  const columns: TableColumns<SynchronizationsTableType>[] = useMemo(() => {
    function getStatusTranslation(status: EventJobStatus): { text: string; icon: ReactNode } {
      switch (status) {
        case EventJobStatus.Error:
          return { text: t('synchronizations.error'), icon: <CloseIcon color='var(--color-error)' /> };
        case EventJobStatus.Finished:
          return { text: t('synchronizations.finished'), icon: <CheckIcon color='var(--color-success)' /> };
        case EventJobStatus.Running:
          return { text: t('synchronizations.running'), icon: <UpdateIcon className='svg-icon spinning' /> };
        default:
          return { text: '', icon: <></> };
      }
    }

    return [
      {
        header: t('synchronizations.startedAt'),
        accessorKey: 'startedAt',
        id: 'startedAt',
        size: 150,
      },
      {
        header: t('synchronizations.finishedAt'),
        accessorKey: 'finishedAt',
        id: 'finishedAt',
        size: 150,
        cell: ({ row }) => {
          return row.original.status === EventJobStatus.Running ? '' : row.original.finishedAt;
        },
      },
      {
        header: t('synchronizations.created'),
        accessorKey: 'created',
        id: 'created',
        size: 70,
      },
      {
        header: t('synchronizations.updated'),
        accessorKey: 'updated',
        id: 'updated',
        size: 70,
      },
      {
        header: t('synchronizations.deleted'),
        accessorKey: 'deleted',
        id: 'deleted',
        size: 70,
      },
      {
        header: t('common.status'),
        accessorKey: 'status',
        id: 'status',
        size: 50,
        cell: ({ row }) => {
          const { text, icon } = getStatusTranslation(row.original.status);
          return (
            <Tooltip triggerStyle={{ display: 'inline' }} content={text}>
              {icon}
            </Tooltip>
          );
        },
      },
    ];
  }, [t]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      refetch();
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [refetch]);

  return (
    <Table<SynchronizationsTableType>
      columns={columns}
      data={data}
      showBorderRadius
      showShadow
      isOnWhite={false}
      showActionBar
      actionBarSettings={{
        extendedActionsRight: (
          <Button onClick={onSync} icon={<UpdateIcon />} disabled={hasRunningEventJobs}>
            {t('synchronizations.syncNow')}
          </Button>
        ),
      }}
    />
  );
};
